import React, { useRef } from 'react'
import type { MutableRefObject, ReactElement} from 'react';

import { Box, Paper, Popper, useTheme } from '@mui/material'
import classNames from 'classnames'

import { useClickOutsideListener } from '@acre/utils/core'

import withDisabled from '../../hoc/withDisabled/withDisabled'
import useFieldDisabledState from '../../hooks/useFieldDisabledState'
import type { Variant } from '../../utils/constants';
import { BUTTON_OVERFLOW, BUTTON_TYPE_BUTTON } from '../../utils/constants'
import type { ButtonTheme, ColourId } from '../../utils/types'
import Button from '../Button/Button'
import Icon from '../Icon/Icon'
import { IconTypes } from '../Icon/Icon.helpers'
import PopOver from '../PopOver/PopOver'
import PopOverItem from '../PopOverItem/PopOverItem'
import { ButtonOverflowWrapper, StyledText } from './ButtonOverflow.styles'

export type ButtonOverflowProps = React.PropsWithChildren<{
  id: string
  isOpen: boolean
  onClick: (() => void) | ((e: MouseEvent) => void)
  onClose: (() => void) | ((e: MouseEvent) => void)
  disabled?: boolean
  icon?: IconTypes
  customIcon?: ReactElement
  isLoading?: boolean
  colourID?: ColourId
  variant?: Variant
  showText?: boolean
  buttonText?: string
  style?: ButtonTheme
  overflowMargin?: boolean
  fontColor?: string
  fontSize?: string
  showLeftMargin?: boolean
  maxSize?: boolean
  overflowYVisible?: boolean
  // usePopper makes the component use the MUI Popper component rather than the Acre Popover
  // which means it can be used inside the MUI grid, for instance
  usePopper?: boolean
}>

const ButtonOverflow = ({
  id,
  isOpen,
  children,
  onClick,
  onClose,
  disabled: disabledProp,
  icon = IconTypes.Overflow,
  customIcon,
  isLoading,
  colourID,
  variant = 'default',
  showText = false,
  buttonText,
  style = BUTTON_OVERFLOW,
  overflowMargin,
  fontColor,
  fontSize,
  showLeftMargin = false,
  maxSize,
  overflowYVisible,
  usePopper = false,
}: ButtonOverflowProps) => {
  const theme = useTheme()
  const className = classNames({ open: isOpen })
  const popoverRef: MutableRefObject<any> = useRef<HTMLDivElement>(null)
  useClickOutsideListener(popoverRef, onClose, isOpen)
  const disabled = useFieldDisabledState(disabledProp)

  const dropDownElements = React.Children.map(children, (child) => <PopOverItem>{child}</PopOverItem>)

  return (
    <ButtonOverflowWrapper ref={(elem) => (popoverRef.current = elem)} showLeftMargin={showLeftMargin}>
      <Button
        id={id}
        type={BUTTON_TYPE_BUTTON}
        buttonStyle={style}
        onClick={isOpen ? onClose : onClick}
        className={className}
        disabled={disabled}
        isLoading={isLoading}
        variant={variant}
        colourID={colourID}
      >
        {showText ? (
          <>
            <StyledText fontSize={fontSize} fontColor={fontColor}>
              {buttonText}
            </StyledText>
            {customIcon && (
              <Box display="flex" justifyContent="center" alignItems="center" ml={theme.spacers.size4}>
                {customIcon}
              </Box>
            )}
          </>
        ) : (
          customIcon || <Icon name={icon} />
        )}
      </Button>
      {usePopper ? (
        <Popper open={isOpen} anchorEl={popoverRef.current}>
          <Paper>{dropDownElements}</Paper>
        </Popper>
      ) : (
        <PopOver
          isVisible={isOpen}
          overflowMargin={overflowMargin}
          maxSize={maxSize}
          overflowYVisible={overflowYVisible}
          zIndex={theme.display.alertZIndex}
        >
          {dropDownElements}
        </PopOver>
      )}
    </ButtonOverflowWrapper>
  )
}

export default withDisabled(ButtonOverflow)
